// Add Expense modal (2026-08-25).
//
// The Expenses page used to inline-expand the form under the header,
// which pushed the KPI row + Recent Expenses list down and broke the
// premium Finance layout. Moving the form into a proper Dialog keeps
// the page stable behind a centered modal and matches every other
// premium dialog in the app (Change Password, Duplicate Class,
// SubjectDetailsDialog).
//
// The form body, field set, mutation, and success/error handling are
// lifted from the previous inline implementation verbatim — no new
// fields, no new API surface, no changed serialization. Fields:
//   title, amount, category_id, account_id, expense_date, vendor,
//   description.
//
// Success: shows the app toast + calls `onSuccess()` so the parent can
// re-fetch and close the dialog.
// Error:   keeps modal open, preserves values, shows error toast.

import React, { useState, useEffect } from "react";
import {
  Box, Grid, Typography, Button, IconButton, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, FormControl, Select, MenuItem,
  InputAdornment, CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import PriceChangeOutlinedIcon from "@mui/icons-material/PriceChangeOutlined";
import { useStoreExpenseMutation } from "../../Api/attoDeskApi";
import { useNotifier } from "../../Core/Notifier";
import AppDatePicker from "../../Components/DatePicker/AppDatePicker";
import { fin } from "../../Components/Finance";
import dayjs from "dayjs";

interface CategoryOpt { id: number | string; name: string; color?: string | null; }
interface AccountOpt  { id: number | string; name: string; }

interface Props {
  open: boolean;
  onClose: () => void;
  /** Called after a successful save so the parent can refetch its lists. */
  onSuccess: () => void;
  categories: CategoryOpt[];
  accounts: AccountOpt[];
}

const AddExpenseDialog: React.FC<Props> = ({ open, onClose, onSuccess, categories, accounts }) => {
  const { showMessage, showErrorMessage } = useNotifier();
  const [storeExpense, { isLoading: saving }] = useStoreExpenseMutation();

  const [title, setTitle] = useState("");
  const [amount, setAmount] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [accountId, setAccountId] = useState("");
  const [expenseDate, setExpenseDate] = useState(dayjs().format("YYYY-MM-DD"));
  const [vendor, setVendor] = useState("");
  const [description, setDescription] = useState("");

  // Reset form only when the modal opens fresh (not on every render).
  // On close-after-error we keep the user's entries so they can retry.
  useEffect(() => {
    if (open) {
      // Only reset if previously closed cleanly (no state leaks).
      // Keeping this simple: fresh open resets. Error path won't
      // toggle `open`, so entries are preserved by definition.
      setTitle(""); setAmount(""); setCategoryId(""); setAccountId("");
      setExpenseDate(dayjs().format("YYYY-MM-DD"));
      setVendor(""); setDescription("");
    }
  }, [open]);

  const isValid = !!(title && amount && categoryId && expenseDate) && Number(amount) > 0;

  const handleSubmit = async () => {
    if (!isValid) {
      showErrorMessage("Title, amount, category, and date are required");
      return;
    }
    const cat = categories.find((c) => String(c.id) === categoryId);
    try {
      const r = await storeExpense({
        title, amount: Number(amount),
        category: cat?.name || "Other",
        category_id: Number(categoryId),
        account_id: accountId ? Number(accountId) : undefined,
        expense_date: expenseDate,
        vendor, description,
      }).unwrap();
      if (r.status) {
        showMessage(r.message);
        onSuccess();
        onClose();
      } else {
        // API returned a business error — keep modal open, preserve values.
        showErrorMessage(r.message);
      }
    } catch (err: any) {
      showErrorMessage(err?.data?.message || "Failed to save expense");
    }
  };

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      maxWidth="sm" fullWidth
      PaperProps={{
        sx: {
          borderRadius: { xs: 0, sm: `${fin.radius.xl}px` },
          border: `1px solid ${fin.color.border}`,
          boxShadow: "0 12px 40px rgba(23,50,77,0.14)",
          m: { xs: 0, sm: 2 },
          width: { xs: "100%", sm: "auto" },
          height: { xs: "100%", sm: "auto" },
          maxHeight: { xs: "100%", sm: "85vh" },
        },
      }}
      aria-labelledby="add-expense-title"
    >
      <DialogTitle id="add-expense-title" sx={{ px: 3, py: 2, borderBottom: `1px solid ${fin.color.border}` }}>
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
          <Box sx={{
            width: 40, height: 40, borderRadius: `${fin.radius.md}px`,
            bgcolor: fin.color.surfaceBlue, color: fin.color.blue,
            display: "flex", alignItems: "center", justifyContent: "center",
            flexShrink: 0, "& svg": { fontSize: 22 },
          }}>
            <PriceChangeOutlinedIcon />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: 17, fontWeight: 800, color: fin.color.navy, lineHeight: 1.2 }}>
              Add Expense
            </Typography>
            <Typography sx={{ fontSize: 12, color: fin.color.muted, mt: 0.25 }}>
              Record a new business expense.
            </Typography>
          </Box>
          <IconButton size="small" onClick={onClose} disabled={saving} aria-label="Close">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ px: 3, py: 2.5 }}>
        <Typography sx={{
          fontSize: 10.5, fontWeight: 800, color: fin.color.muted,
          textTransform: "uppercase", letterSpacing: "0.08em", mb: 1.25,
        }}>
          Expense Details
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={12} sm={7}>
            <Typography sx={{ fontSize: 11, fontWeight: 800, color: fin.color.muted, textTransform: "uppercase", letterSpacing: "0.06em", mb: 0.5 }}>Title *</Typography>
            <TextField
              size="small" fullWidth autoFocus
              value={title} onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Office supplies, Utilities…"
              InputProps={{ sx: { borderRadius: "10px", fontSize: 13 } }}
            />
          </Grid>
          <Grid item xs={12} sm={5}>
            <Typography sx={{ fontSize: 11, fontWeight: 800, color: fin.color.muted, textTransform: "uppercase", letterSpacing: "0.06em", mb: 0.5 }}>Amount *</Typography>
            <TextField
              size="small" fullWidth type="number"
              value={amount} onChange={(e) => setAmount(e.target.value)}
              placeholder="0.00"
              inputProps={{ min: 0, step: "0.01" }}
              InputProps={{
                startAdornment: <InputAdornment position="start"><AttachMoneyIcon sx={{ fontSize: 16, color: fin.color.muted }} /></InputAdornment>,
                sx: { borderRadius: "10px", fontSize: 13 },
              }}
            />
          </Grid>

          <Grid item xs={12} sm={7}>
            <Typography sx={{ fontSize: 11, fontWeight: 800, color: fin.color.muted, textTransform: "uppercase", letterSpacing: "0.06em", mb: 0.5 }}>Category *</Typography>
            <FormControl size="small" fullWidth>
              <Select
                value={categoryId} onChange={(e) => setCategoryId(String(e.target.value))} displayEmpty
                sx={{ borderRadius: "10px", fontSize: 13 }}
              >
                <MenuItem value="" disabled sx={{ fontSize: 12 }}>Select category</MenuItem>
                {categories.map((c) => (
                  <MenuItem key={c.id} value={String(c.id)} sx={{ fontSize: 12 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: c.color || fin.color.muted }} />
                      {c.name}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={5}>
            <Typography sx={{ fontSize: 11, fontWeight: 800, color: fin.color.muted, textTransform: "uppercase", letterSpacing: "0.06em", mb: 0.5 }}>Date *</Typography>
            <AppDatePicker
              size="small" fullWidth value={expenseDate} onChange={setExpenseDate}
              sx={{ borderRadius: "10px", fontSize: 13 }}
            />
          </Grid>

          <Grid item xs={12}>
            <Typography sx={{ fontSize: 11, fontWeight: 800, color: fin.color.muted, textTransform: "uppercase", letterSpacing: "0.06em", mb: 0.5 }}>Description</Typography>
            <TextField
              size="small" fullWidth multiline minRows={2}
              value={description} onChange={(e) => setDescription(e.target.value)}
              placeholder="Optional details about this expense"
              InputProps={{ sx: { borderRadius: "10px", fontSize: 13 } }}
            />
          </Grid>
        </Grid>

        <Typography sx={{
          fontSize: 10.5, fontWeight: 800, color: fin.color.muted,
          textTransform: "uppercase", letterSpacing: "0.08em", mt: 2.5, mb: 1.25,
        }}>
          Payment Details
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={12} sm={7}>
            <Typography sx={{ fontSize: 11, fontWeight: 800, color: fin.color.muted, textTransform: "uppercase", letterSpacing: "0.06em", mb: 0.5 }}>Account</Typography>
            <FormControl size="small" fullWidth>
              <Select
                value={accountId} onChange={(e) => setAccountId(String(e.target.value))} displayEmpty
                sx={{ borderRadius: "10px", fontSize: 13 }}
              >
                <MenuItem value="" sx={{ fontSize: 12 }}>Default Account</MenuItem>
                {accounts.map((a) => (
                  <MenuItem key={a.id} value={String(a.id)} sx={{ fontSize: 12 }}>{a.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={5}>
            <Typography sx={{ fontSize: 11, fontWeight: 800, color: fin.color.muted, textTransform: "uppercase", letterSpacing: "0.06em", mb: 0.5 }}>Vendor</Typography>
            <TextField
              size="small" fullWidth value={vendor} onChange={(e) => setVendor(e.target.value)}
              placeholder="Vendor name"
              InputProps={{ sx: { borderRadius: "10px", fontSize: 13 } }}
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, borderTop: `1px solid ${fin.color.border}` }}>
        <Button
          onClick={onClose} disabled={saving}
          sx={{
            textTransform: "none", fontWeight: 700, borderRadius: 2,
            color: fin.color.muted, px: 2,
            "&:hover": { color: fin.color.navy, bgcolor: `${fin.color.surfaceBlue}` },
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained" onClick={handleSubmit}
          disabled={!isValid || saving}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <AddIcon sx={{ fontSize: 18 }} />}
          sx={{
            textTransform: "none", fontWeight: 800, borderRadius: 2,
            bgcolor: fin.color.blue, boxShadow: "none", px: 2.5,
            "&:hover": { bgcolor: fin.color.navy, boxShadow: `0 6px 14px rgba(23,50,77,0.14)` },
            "&.Mui-disabled": { bgcolor: `${fin.color.surfaceBlue}`, color: "#fff" },
          }}
        >
          {saving ? "Saving…" : "Save Expense"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddExpenseDialog;
